import { useState, useEffect, useMemo, useRef } from 'react';
import {
  listFiles,
  uploadFile,
  updateFile,
  renameFile,
  deleteFile,
  getDownloadUrl,
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  moveFiles,
  copyFiles,
} from '../api/repository';
import './FileRepository.css';
import Button from './Button';
import RepoFolderTree from './RepoFolderTree';
import {
  GROUP_OPTIONS,
  allTags,
  buildFolderTree,
  canGoBack,
  canGoForward,
  childPath,
  crumbsOf,
  fileCountsByFolder,
  filterFiles,
  groupFiles,
  iconOf,
  initialHistory,
  kindChipsFor,
  navBack,
  navForward,
  navPath,
  navPush,
  nextSort,
  parentOf,
  parseSort,
  rewriteHistory,
  sortFiles,
  subfoldersOf,
} from './fileOrganize';

// Exported for the composer's attachment chips, which show the same size and
// per-extension glyph for a file on its way into this same repository.
export function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Kept exported from here because `PromptAttachments` and `FilePickerModal` import
// it from this module. The extension table itself lives in `fileOrganize.js`, so
// the glyph and the kind filter cannot disagree about what a `.stl` is.
export function fileIcon(type, name) {
  return iconOf(name);
}

function fileTypeBadge(name) {
  return name?.split('.').pop()?.toUpperCase() || 'FILE';
}

const SORT_COLUMNS = [
  { field: 'fileName', label: 'Name' },
  { field: 'fileType', label: 'Type' },
  { field: 'fileSize', label: 'Size' },
  { field: 'createdAt', label: 'Uploaded' },
];

export default function FileRepository({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('createdAt-desc');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [error, setError] = useState(null);
  const [, setActiveMenu] = useState(null);
  // Organization view state. All session-local: a folder is where files ARE, but
  // which folder you were looking at is not worth persisting across sessions.
  // The open folder lives in a NAVIGATION HISTORY rather than a bare string, so
  // Back and Forward mean what they do in a file explorer.
  const [history, setHistory] = useState(() => initialHistory(''));
  const folderPath = navPath(history);
  const [kind, setKind] = useState('all');
  const [activeTags, setActiveTags] = useState([]);
  const [groupBy, setGroupBy] = useState('none');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [taggingId, setTaggingId] = useState(null);
  const [tagValue, setTagValue] = useState('');
  // Which folder ROW a drag is currently over. State rather than a ref because
  // the row's highlight renders from it — the opposite of `dragIdsRef` below.
  const [dropTarget, setDropTarget] = useState(null);
  const fileInputRef = useRef(null);
  const renameInputRef = useRef(null);
  const tagInputRef = useRef(null);
  // The rows being dragged. A ref, not state: `dragstart` → `drop` is one gesture
  // and nothing renders from it, so a state write per drag would re-render the
  // whole table under the cursor.
  const dragIdsRef = useRef([]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen]);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  useEffect(() => {
    if (taggingId) tagInputRef.current?.focus();
  }, [taggingId]);

  async function load() {
    setLoading(true);
    try {
      // One round trip each; the folder list is small and the file list is
      // deliberately unpaginated (a repository view that hides files is the
      // defect this endpoint's own comment warns about).
      const [data, folderRows] = await Promise.all([listFiles(), listFolders()]);
      setFiles(data);
      setFolders(folderRows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setUploading(true);
    setError(null);
    const uploadedIds = [];
    for (const file of selected) {
      try {
        setUploadProgress({ name: file.name, pct: 0 });
        const fileId = await uploadFile(file, (pct) => setUploadProgress({ name: file.name, pct }));
        if (fileId) uploadedIds.push(fileId);
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    // An upload always lands at the root (the backend's writers set no
    // folderPath), so file it into the folder the user is looking at — dropping
    // it at the root while they are inside a folder reads as the upload having
    // failed. Best-effort: a failed move leaves the file safely at the root.
    if (folderPath && uploadedIds.length) {
      try {
        await moveFiles(uploadedIds, folderPath);
      } catch {
        /* the file uploaded; only its filing failed */
      }
    }
    setUploading(false);
    setUploadProgress(null);
    fileInputRef.current.value = '';
    await load();
  }

  async function handleRename(fileId) {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await renameFile(fileId, renameValue.trim());
      setFiles((prev) =>
        prev.map((f) => (f.fileId === fileId ? { ...f, fileName: renameValue.trim() } : f))
      );
    } catch (err) {
      setError(err.message);
    }
    setRenamingId(null);
  }

  async function handleSaveTags(fileId) {
    // Comma-separated is the lightest thing that works inline; the backend
    // normalizes (trim, collapse, dedupe case-insensitively) so a sloppy list is
    // stored tidily rather than refused.
    const tags = tagValue
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    setTaggingId(null);
    try {
      await updateFile(fileId, { tags });
      // Re-read rather than echoing the input: the server may have deduped or
      // re-cased, and showing the raw input would disagree with the next reload.
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(fileId) {
    try {
      await deleteFile(fileId);
      setFiles((prev) => prev.filter((f) => f.fileId !== fileId));
      setSelectedIds((prev) => {
        if (!prev.has(fileId)) return prev;
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    } catch (err) {
      setError(err.message);
    }
    setDeleteId(null);
  }

  async function handleDownload(fileId) {
    try {
      const { downloadUrl, fileName } = await getDownloadUrl(fileId);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      a.click();
    } catch (err) {
      setError(err.message);
    }
    setActiveMenu(null);
  }

  // ── Organization actions ────────────────────────────────────────────────
  async function handleCreateFolder(parent, name) {
    setError(null);
    try {
      await createFolder(childPath(parent, name));
      setFolders(await listFolders());
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRenameFolder(node, name) {
    setError(null);
    const target = childPath(parentOf(node.path), name);
    try {
      await updateFolder(node.folderId, target);
      // A rename rewrites descendants AND the folderPath of every file
      // underneath, so both lists are refetched rather than patched locally.
      await load();
      // Follow the folder if the user was inside it or its subtree, so the view
      // does not silently jump to an empty path that no longer exists. Rewritten
      // across the WHOLE history, not just the current entry — Back would
      // otherwise return to a path the rename has just made nonexistent.
      setHistory((h) => rewriteHistory(h, node.path, target));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeleteFolder(node) {
    setError(null);
    try {
      await deleteFolder(node.folderId);
      await load();
      // The contents moved up one level, so follow them there — and collapse
      // any history entry pointing into the deleted subtree onto the same
      // place, since Back must not return to a folder that no longer exists.
      setHistory((h) => rewriteHistory(h, node.path, parentOf(node.path)));
    } catch (err) {
      setError(err.message);
    }
    setDeleteFolderTarget(null);
  }

  async function handleMove(ids, target) {
    if (!ids.length) return;
    setError(null);
    try {
      await moveFiles(ids, target);
      await load();
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleCopy(ids, target) {
    if (!ids.length) return;
    setError(null);
    try {
      await copyFiles(ids, target);
      await load();
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    }
  }

  // Ctrl/⌘-drag COPIES, plain drag moves — the file-explorer convention, read
  // off the drop event because the modifier can be pressed mid-drag.
  function handleDropOnFolder(target, e) {
    const ids = dragIdsRef.current;
    dragIdsRef.current = [];
    if (e?.ctrlKey || e?.metaKey) handleCopy(ids, target);
    else handleMove(ids, target);
  }

  function handleDragStart(fileId) {
    // Dragging a row inside a selection moves the whole selection; dragging an
    // unselected row moves just that row, without disturbing the selection.
    dragIdsRef.current = selectedIds.has(fileId) ? [...selectedIds] : [fileId];
  }

  // ── Navigation ──────────────────────────────────────────────────────────
  function goTo(path) {
    setHistory((h) => navPush(h, path));
    // A folder's contents are a different list; carrying a selection across
    // would leave a bulk action pointed at rows nobody can see.
    setSelectedIds(new Set());
    // Entering a folder while a search is open is a request to browse THAT
    // folder, so the search that spans every folder has served its purpose.
    setSearch('');
  }

  function goBack() {
    setHistory(navBack);
    setSelectedIds(new Set());
  }

  function goForward() {
    setHistory(navForward);
    setSelectedIds(new Set());
  }

  function toggleSelected(fileId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  function toggleTag(tag) {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }

  // ── Derived view ────────────────────────────────────────────────────────
  const [sortField, sortDir] = parseSort(sort);
  // Searching looks across the WHOLE repository, not just the open folder: a
  // user who types a name is asking where it is, and answering "not in this
  // folder" by showing nothing is the least useful true answer available.
  const searching = search.trim().length > 0;
  const visible = useMemo(() => {
    const filtered = filterFiles(files, {
      folderPath: searching ? null : folderPath,
      search,
      kinds: kind,
      tags: activeTags,
    });
    return sortFiles(filtered, sortField, sortDir);
  }, [files, folderPath, search, searching, kind, activeTags, sortField, sortDir]);

  const groups = useMemo(() => groupFiles(visible, groupBy), [visible, groupBy]);
  const counts = useMemo(() => fileCountsByFolder(files), [files]);
  const tree = useMemo(() => buildFolderTree(folders, counts), [folders, counts]);
  const chips = useMemo(() => kindChipsFor(files), [files]);
  const tagOptions = useMemo(() => allTags(files), [files]);
  const crumbs = crumbsOf(folderPath);
  // The current folder's own subfolders, listed ABOVE its files. "All files" is
  // the parent of the top-level folders, so at the root these are those. Hidden
  // while searching: a search spans every folder and returns files, so folder
  // rows there would be navigation mixed into a result set.
  const subfolders = useMemo(
    () => (searching ? [] : subfoldersOf(folders, folderPath, counts)),
    [folders, folderPath, counts, searching]
  );

  const visibleIds = visible.map((f) => f.fileId);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  // Prune ids that have scrolled out of existence (a delete, a filter change),
  // identity-stable so the effect does not loop on its own dependency.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const live = new Set(files.map((f) => f.fileId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [files]);

  if (!isOpen) return null;

  const selectedCount = selectedIds.size;

  return (
    <div className="repo-overlay" onClick={onClose}>
      <div className="repo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="repo-modal-header">
          <div className="repo-modal-title">
            <span>📁</span>
            <h2>File Repository</h2>
            <span className="repo-count">{files.length} files</span>
          </div>
          <button className="repo-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Toolbar */}
        <div className="repo-modal-toolbar">
          <input
            type="text"
            className="repo-search"
            placeholder="Search all files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <select
            className="repo-sort"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            aria-label="Group files"
          >
            {GROUP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploading ? '⟳ Uploading...' : '⬆ Upload'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
        </div>

        {/* Type + tag filters. Only rendered when there is something to filter,
            so a small repository is not fronted by controls that all empty it. */}
        {(chips.length > 1 || tagOptions.length > 0) && (
          <div className="repo-filters">
            {chips.length > 1 && (
              <div className="repo-chip-row">
                {chips.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`repo-chip${kind === c.value ? ' repo-chip--on' : ''}`}
                    onClick={() => setKind(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            {tagOptions.length > 0 && (
              <div className="repo-chip-row">
                <span className="repo-chip-label">Tags</span>
                {tagOptions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`repo-chip repo-chip--tag${
                      activeTags.includes(t) ? ' repo-chip--on' : ''
                    }`}
                    onClick={() => toggleTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Progress */}
        {uploadProgress && (
          <div className="repo-progress">
            <span>Uploading {uploadProgress.name}</span>
            <div className="repo-progress-bar">
              <div style={{ width: `${uploadProgress.pct}%` }} />
            </div>
            <span>{uploadProgress.pct}%</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="repo-error" onClick={() => setError(null)}>
            {error} <span>✕</span>
          </div>
        )}

        {/* Body: folder rail beside the list */}
        <div className="repo-modal-body">
          <div className="repo-rail">
            <RepoFolderTree
              tree={tree}
              selectedPath={folderPath}
              totalCount={files.length}
              onSelect={goTo}
              onDropFiles={handleDropOnFolder}
              onCreateFolder={handleCreateFolder}
              onRenameFolder={handleRenameFolder}
              onDeleteFolder={(node) => setDeleteFolderTarget(node)}
            />
          </div>

          <div className="repo-list">
            {/* Navigation: Back / Forward / Up, then the breadcrumb. */}
            <div className="repo-nav">
              <div className="repo-nav-btns">
                <button
                  type="button"
                  className="repo-nav-btn"
                  title="Back"
                  disabled={!canGoBack(history)}
                  onClick={goBack}
                >
                  ←
                </button>
                <button
                  type="button"
                  className="repo-nav-btn"
                  title="Forward"
                  disabled={!canGoForward(history)}
                  onClick={goForward}
                >
                  →
                </button>
                <button
                  type="button"
                  className="repo-nav-btn"
                  title="Up one level"
                  disabled={!folderPath}
                  onClick={() => goTo(parentOf(folderPath))}
                >
                  ↑
                </button>
              </div>

              <div className="repo-crumbs">
                {searching ? (
                  <span className="repo-crumb-note">
                    Searching all folders for “{search.trim()}”
                  </span>
                ) : (
                  crumbs.map((c, i) => (
                    <span key={c.path || 'root'} className="repo-crumb-wrap">
                      {i > 0 && <span className="repo-crumb-sep">/</span>}
                      <button
                        type="button"
                        className={`repo-crumb${
                          i === crumbs.length - 1 ? ' repo-crumb--current' : ''
                        }`}
                        onClick={() => goTo(c.path)}
                      >
                        {c.label}
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Bulk bar — only while a selection exists, so it never occupies
                space for an action nobody can take. */}
            {selectedCount > 0 && (
              <div className="repo-bulk">
                <span className="repo-bulk-count">{selectedCount} selected</span>
                {/* Two separate pickers rather than one destination plus a
                    move/copy toggle: they are different operations with
                    different costs (a copy duplicates the bytes), and a toggle
                    makes the destructive-looking one reachable by mis-reading a
                    control rather than by choosing it. The placeholder uses a
                    SENTINEL value, because the root really is `''` and an
                    empty-valued placeholder would be indistinguishable from it. */}
                <label className="repo-bulk-move">
                  Move to
                  <select
                    className="repo-sort"
                    value="__none__"
                    onChange={(e) => handleMove([...selectedIds], e.target.value)}
                  >
                    <option value="__none__" disabled>
                      Choose a folder…
                    </option>
                    <option value="">All files (root)</option>
                    {folders.map((f) => (
                      <option key={f.folderId} value={f.path}>
                        {f.path}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="repo-bulk-move">
                  Copy to
                  <select
                    className="repo-sort"
                    value="__none__"
                    onChange={(e) => handleCopy([...selectedIds], e.target.value)}
                  >
                    <option value="__none__" disabled>
                      Choose a folder…
                    </option>
                    <option value="">All files (root)</option>
                    {folders.map((f) => (
                      <option key={f.folderId} value={f.path}>
                        {f.path}
                      </option>
                    ))}
                  </select>
                </label>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}

            <div className="repo-list-scroll">
              {loading ? (
                <div className="repo-loading">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="repo-skeleton" />
                  ))}
                </div>
              ) : visible.length === 0 && subfolders.length === 0 ? (
                <div className="repo-empty">
                  {search || kind !== 'all' || activeTags.length ? (
                    <p>No files match the current filters</p>
                  ) : folderPath ? (
                    <>
                      <div className="repo-empty-icon">📂</div>
                      <p>This folder is empty</p>
                      <span>Drag files here, or upload into it</span>
                    </>
                  ) : (
                    <>
                      <div className="repo-empty-icon">📁</div>
                      <p>No files yet</p>
                      <span>Upload CAD files, JSON configs, and more</span>
                      <Button variant="primary" onClick={() => fileInputRef.current.click()}>
                        ⬆ Upload your first file
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <table className="repo-table">
                  <thead>
                    <tr>
                      <th className="repo-check-col">
                        <input
                          type="checkbox"
                          className="repo-check"
                          checked={allSelected}
                          aria-label="Select all shown"
                          onChange={(e) =>
                            setSelectedIds(e.target.checked ? new Set(visibleIds) : new Set())
                          }
                        />
                      </th>
                      {SORT_COLUMNS.map((col) => (
                        <th key={col.field}>
                          <button
                            type="button"
                            className={`repo-sort-btn${
                              sortField === col.field ? ' repo-sort-btn--on' : ''
                            }`}
                            onClick={() => setSort(nextSort(sort, col.field))}
                          >
                            {col.label}
                            <span className="repo-sort-caret">
                              {sortField === col.field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                            </span>
                          </button>
                        </th>
                      ))}
                      <th>Tags</th>
                      <th></th>
                    </tr>
                  </thead>
                  {/* Folders first, as in every file explorer — and as their own
                      tbody so they sit above the groupings rather than inside
                      one, which would file them under an extension they do not
                      have. */}
                  {subfolders.length > 0 && (
                    <tbody>
                      {subfolders.map((folder) => (
                        <tr
                          key={folder.path}
                          className={`repo-folder-row${
                            dropTarget === folder.path ? ' repo-folder-row--dragover' : ''
                          }`}
                          onDoubleClick={() => goTo(folder.path)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDropTarget(folder.path);
                          }}
                          onDragLeave={() => setDropTarget(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDropTarget(null);
                            handleDropOnFolder(folder.path, e);
                          }}
                        >
                          <td className="repo-check-col" />
                          <td className="repo-name-cell">
                            <span className="repo-file-icon">📁</span>
                            {/* A button, not a bare row handler: this is the
                                keyboard route into a folder, and double-click
                                alone would leave the list unnavigable without a
                                mouse. */}
                            <button
                              type="button"
                              className="repo-folder-name"
                              title={`Open ${folder.path}`}
                              onClick={() => goTo(folder.path)}
                            >
                              {folder.name}
                            </button>
                          </td>
                          <td>
                            <span className="repo-type-badge repo-type-badge--folder">FOLDER</span>
                          </td>
                          <td className="repo-muted">
                            {folder.fileCount === 1 ? '1 file' : `${folder.fileCount} files`}
                          </td>
                          <td className="repo-muted">—</td>
                          <td />
                          <td />
                        </tr>
                      ))}
                    </tbody>
                  )}
                  {groups.map((group) => (
                    <tbody key={group.key}>
                      {group.label && (
                        <tr className="repo-group-row">
                          <td colSpan={7}>
                            {group.label}
                            <span className="repo-group-count">{group.files.length}</span>
                          </td>
                        </tr>
                      )}
                      {group.files.map((file) => (
                        <tr
                          key={file.fileId}
                          className={selectedIds.has(file.fileId) ? 'repo-row--selected' : ''}
                          draggable
                          onDragStart={() => handleDragStart(file.fileId)}
                        >
                          <td className="repo-check-col">
                            <input
                              type="checkbox"
                              className="repo-check"
                              checked={selectedIds.has(file.fileId)}
                              aria-label={`Select ${file.fileName}`}
                              onChange={() => toggleSelected(file.fileId)}
                            />
                          </td>
                          <td className="repo-name-cell">
                            <span className="repo-file-icon">
                              {fileIcon(file.fileType, file.fileName)}
                            </span>
                            {renamingId === file.fileId ? (
                              <input
                                ref={renameInputRef}
                                className="repo-rename-input"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRename(file.fileId);
                                  if (e.key === 'Escape') setRenamingId(null);
                                }}
                                onBlur={() => handleRename(file.fileId)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span className="repo-filename" title={file.fileName}>
                                {file.fileName}
                              </span>
                            )}
                            {/* Where a search result actually lives — without it a
                                repository-wide search says a file exists but not where. */}
                            {searching && file.folderPath && (
                              <button
                                type="button"
                                className="repo-in-folder"
                                title={`Go to ${file.folderPath}`}
                                onClick={() => {
                                  setSearch('');
                                  goTo(file.folderPath);
                                }}
                              >
                                {file.folderPath}
                              </button>
                            )}
                          </td>
                          <td>
                            <span className="repo-type-badge">{fileTypeBadge(file.fileName)}</span>
                          </td>
                          <td className="repo-muted">{formatSize(file.fileSize)}</td>
                          <td className="repo-muted">{formatDate(file.createdAt)}</td>
                          <td className="repo-tags-cell">
                            {taggingId === file.fileId ? (
                              <input
                                ref={tagInputRef}
                                className="repo-rename-input"
                                placeholder="steel, draft"
                                value={tagValue}
                                onChange={(e) => setTagValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveTags(file.fileId);
                                  if (e.key === 'Escape') setTaggingId(null);
                                }}
                                onBlur={() => handleSaveTags(file.fileId)}
                              />
                            ) : (
                              <button
                                type="button"
                                className="repo-tag-edit"
                                title="Edit tags"
                                onClick={() => {
                                  setTaggingId(file.fileId);
                                  setTagValue((file.tags || []).join(', '));
                                }}
                              >
                                {file.tags?.length ? (
                                  file.tags.map((t) => (
                                    <span key={t} className="repo-tag">
                                      {t}
                                    </span>
                                  ))
                                ) : (
                                  <span className="repo-tag-add">+ tag</span>
                                )}
                              </button>
                            )}
                          </td>
                          <td className="repo-actions-cell">
                            <div className="repo-action-btns">
                              <button
                                className="repo-action-btn"
                                title="Download"
                                onClick={() => handleDownload(file.fileId)}
                              >
                                ⬇
                              </button>
                              <button
                                className="repo-action-btn"
                                title="Rename"
                                onClick={() => {
                                  setRenamingId(file.fileId);
                                  setRenameValue(file.fileName);
                                }}
                              >
                                ✏
                              </button>
                              <button
                                className="repo-action-btn repo-action-delete"
                                title="Delete"
                                onClick={() => setDeleteId(file.fileId)}
                              >
                                🗑
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteId && (
        <div className="repo-confirm-overlay" onClick={() => setDeleteId(null)}>
          <div className="repo-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Delete file?</h3>
            <p>This will permanently remove the file from your repository.</p>
            <div className="repo-confirm-actions">
              <Button variant="secondary" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteId)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Folder delete confirmation. Says what happens to the contents, because
          "delete" next to a folder full of files invites the worst assumption. */}
      {deleteFolderTarget && (
        <div className="repo-confirm-overlay" onClick={() => setDeleteFolderTarget(null)}>
          <div className="repo-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Delete “{deleteFolderTarget.name}”?</h3>
            <p>
              No files are deleted. Anything inside moves
              {parentOf(deleteFolderTarget.path)
                ? ` up to “${parentOf(deleteFolderTarget.path)}”.`
                : ' back to the top level.'}
            </p>
            <div className="repo-confirm-actions">
              <Button variant="secondary" onClick={() => setDeleteFolderTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDeleteFolder(deleteFolderTarget)}>
                Delete folder
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
